import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { EditGeoEventProvisionalSubmissionResponse } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { useGeoEventStatusFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventStatusFormFieldChanges';
import { useGeoEventMetadataFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventMetadataFormFieldChanges';
import { useGeoEventGeolocationFormFieldChanges } from 'features/geoEvents/hooks/useGeoEventGeolocationFormFieldChanges';
import { getDateFormatted } from 'shared/dates';
import {
  FormValues,
  convertSubmittedDataToFormValues,
} from '../../EditAutomatedGeoEventForm.utils';

const ReviewEditChangesList = ({
  geoEvent,
}: {
  geoEvent: Wildfire;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const statusChanges = useGeoEventStatusFormFieldChanges({ geoEvent });
  const metadataChanges = useGeoEventMetadataFormFieldChanges({ geoEvent });
  const geolocationChanges = useGeoEventGeolocationFormFieldChanges({
    geoEvent,
  });
  const changes = [
    statusChanges.status,
    metadataChanges.reporterManaged,
    metadataChanges.reporterOnlyNotes,
    geolocationChanges.address,
    geolocationChanges.regions,
    geolocationChanges.lat,
    geolocationChanges.lng,
    geolocationChanges.distance,
  ].filter((change): change is string => Boolean(change));

  if (changes.length === 0) return null;

  return (
    <Alert severity="warning" data-testid="changelog-alert">
      <AlertTitle>{t('reviewSubmission.geoEventChanges.title')}</AlertTitle>
      {changes.map((change) => (
        <Typography key={change}>{change}</Typography>
      ))}
    </Alert>
  );
};

type ReviewEditChangesProps = {
  geoEvent: Wildfire;
  reviewSubmission: EditGeoEventProvisionalSubmissionResponse;
};

export const ReviewEditChanges = ({
  geoEvent,
  reviewSubmission,
}: ReviewEditChangesProps): JSX.Element => {
  const { t } = useTranslation();

  // `name` is the only field this form can't edit, so it comes from the
  // geoEvent. Everything else, lat/lng included, comes from the submitted data
  // so a reviewer sees the location move they're approving.
  const methods = useForm<FormValues & { name: string }>({
    defaultValues: {
      ...convertSubmittedDataToFormValues(
        reviewSubmission.submittedData,
        geoEvent,
      ),
      name: geoEvent.name,
    },
  });

  return (
    <Stack spacing={1.5}>
      <Alert severity="info">
        {t('reviewSubmission.submittedByEditGeoEvent', {
          firstName: reviewSubmission.createdBy.firstName,
          lastName: reviewSubmission.createdBy.lastName,
          date: getDateFormatted(reviewSubmission.dateCreated, {
            includeYear: true,
            includeTimeZone: true,
          }),
        })}
      </Alert>

      <FormProvider {...methods}>
        <ReviewEditChangesList geoEvent={geoEvent} />
      </FormProvider>
    </Stack>
  );
};
